<template>
  <div
    class="comment-input-section"
    :class="{ 'comment-input-section--active': !disabledSubmitButton }"
  >
    <div class="comment-input-section__textarea__container">
      <div class="comment-input-section__textarea__inner">
        <div class="comment-input-section__textarea__dummy">{{ comment + '\n' }}</div>
        <AtomTextArea
          class="comment-input-section__textarea"
          v-model="comment"
          :id="'comment'"
          :focusOnMount="true"
          :placeholder="$tc('RaceVideoPage.highlights.comment.placeholder')"
        />
        <p v-if="postX" class="comment-input-section__hashtag">{{ hashtag }}</p>
      </div>
    </div>

    <div class="comment-input-section__buttons">
      <div class="comment-input-section__sns">
        <mission-sns-button-parts
          :postX="postX"
          :postInstagram="postInstagram"
          @onChangePostX="postX = !postX"
          @onChangePostInstagram="postInstagram = !postInstagram"
        />
      </div>

      <div class="comment-input-section__buttons__comment">
        <div class="comment-input-section__count">
          <comment-count-parts :comment="comment" :commentLength="commentLength" />
        </div>
        <button
          class="comment-input-section__button comment-input-section__button--cancel"
          @click="handleCancelClicked"
        >
          <mission-cancel-icon />
        </button>

        <button
          :disabled="disabledSubmitButton"
          class="comment-input-section__button comment-input-section__button--submit"
          @click="onPostComment"
        >
          <mission-submit-icon />
        </button>
      </div>
    </div>

    <!-- 入力キャンセル確認モーダル -->
    <confirm-modal-section
      class="comment-input-section__modal"
      v-if="enabledConfirmModal"
      :message="$tc('RaceVideoPage.highlights.comment.inputCancelModal.inputCancelMessage')"
      :confirmButtonLabel="
        $tc('RaceVideoPage.highlights.comment.inputCancelModal.confirmButtonLabel')
      "
      :cancelButtonLabel="
        $tc('RaceVideoPage.highlights.comment.inputCancelModal.cancelButtonLabel')
      "
      @confirm="cancelInput"
      @cancel="enabledConfirmModal = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from '@vue/composition-api'
import twitterText from 'twitter-text'
import AtomTextArea from '@/components/atoms/input/AtomTextArea.vue'
import ConfirmModalSection from '@/components/common/modal/ConfirmModalSection.vue'
import MissionSnsButtonParts from '@/components/MissionPage/MissionRankingPane/parts/MissionSnsButtonParts.vue'
import MissionCancelIcon from '@/components/MissionPage/MissionRankingPane/parts/icons/MissionCancelIcon.vue'
import MissionSubmitIcon from '@/components/MissionPage/MissionRankingPane/parts/icons/MissionSubmitIcon.vue'
import CommentCountParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/CommentCountParts.vue'
import Const from '@/util/Const'

export default defineComponent({
  name: 'MissionRankingShareInputSection',
  components: {
    CommentCountParts,
    MissionSubmitIcon,
    MissionCancelIcon,
    MissionSnsButtonParts,
    ConfirmModalSection,
    AtomTextArea,
  },
  props: {
    /**
     * 対象ハイライトの入力状態
     */
    targetEnableInput: {
      type: Boolean,
      default: false,
    },
    /**
     * 対象ハイライトに対する入力コメント
     */
    targetInputHighlightComment: {
      type: String,
      default: '',
    },
    hasShare: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const hashtag = process.env.VUE_APP_X_HASH_TAG
    const postX = ref(false)
    const postInstagram = ref(false)

    // コメントキャンセル確認用モーダル
    const enabledConfirmModal = ref<boolean>(false)

    // 入力中のコメント
    const comment = ref('')

    /**
     * コメントの文字数
     */
    const commentLength = computed(() => {
      if (postX.value) {
        return twitterText.parseTweet(`${comment.value}\n${hashtag}`).weightedLength
      }
      return twitterText.parseTweet(comment.value).weightedLength
    })

    /**
     * コメント入力画面のキャンセルボタンが押下されたときの処理
     */
    const handleCancelClicked = () => {
      enabledConfirmModal.value = true
    }

    /**
     * 確定ボタンを押せるかどうか。下記の状態だと押せないようにする。
     * - コメントが空の場合
     * - SNS投稿ボタンがどちらも押されていない場合
     */
    const disabledSubmitButton = computed(
      () =>
        !comment.value.replace(/\s+/g, '') ||
        (!postInstagram.value && !postX.value) ||
        commentLength.value > Const.MAX_HIGHLIGHT_COMMENT_LENGTH,
    )

    return {
      hashtag,
      comment,
      commentLength,
      enabledConfirmModal,
      handleCancelClicked,
      postX,
      postInstagram,
      disabledSubmitButton,
    }
  },
  methods: {
    /**
     * コメント投稿ボタンを押したことを親に伝える
     */
    onPostComment() {
      // 入力状態でコメントがある場合
      if (!this.disabledSubmitButton) {
        this.$emit('onClickPostSns', this.comment, this.postX, this.postInstagram)
      } else {
        // 入力状態にする
        this.startInput()
      }
    },
    /**
     * 入力モード開始
     */
    startInput() {
      this.$emit('onToggleEnabledInput', true)
    },
    /**
     * 入力解除
     */
    cancelInput() {
      this.enabledConfirmModal = false
      this.$emit('onToggleEnabledInput', false)
    },
  },
})
</script>

<style lang="scss" scoped>
.comment-input-section {
  display: flex;
  flex-flow: column nowrap;
  align-content: flex-start;
  justify-items: flex-start;
  width: 100%;
  background: $black;
  border: 1px solid $black90;
  border-radius: 4px;

  &__textarea {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    min-height: 0;
    padding: 0;
    overflow: hidden;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 12px;
    line-height: 1.67;
    letter-spacing: 0.02em;
    color: $white;
    overflow-wrap: break-word;
    background-color: $black;
    border: none;
    outline: none;
    resize: none;

    &:focus {
      background: transparent;
    }

    &::placeholder {
      font-family: $fontSignup;
      font-weight: 500;
      font-size: 12px;
      line-height: 1.67;
      letter-spacing: 0.02em;
      color: $white50;
    }

    &__container {
      padding: 12px 12px 6px;
      height: 6.51em;
      overflow-y: auto;
      font-size: 12px;
      line-height: 1.67;
    }

    &__inner {
      position: relative;
      font-size: 12px;
      line-height: 1.67;
    }

    &__dummy {
      min-height: 12 * 1.67px;
      visibility: hidden;
      overflow-wrap: break-word;
      font-family: $fontSignup;
      font-weight: 500;
      font-size: 12px;
      line-height: 1.67;
      letter-spacing: 0.02em;
      white-space: pre-wrap;
    }
  }

  &__hashtag {
    position: absolute;
    left: 0;
    bottom: -1.67em;
    font-family: $fontSignup;
    font-size: 12px;
    color: $white;
  }

  &__placeholder {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: calc(100% - 44px);
    padding: 0 12px;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 12px;
    line-height: 1.67;
    letter-spacing: 0.02em;
    color: $white50;
  }

  &__buttons {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    width: 100%;
    padding-left: 9px;

    &__comment {
      display: flex;
      align-items: center;
      margin-left: auto;
    }
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;

    &--cancel {
      width: 24px;
      height: 24px;
      margin-left: 10px;
      color: rgba(#c2c2c2, 0.9);
    }

    &--submit {
      opacity: 0.5;
      color: $white;
    }
  }

  &--active {
    .comment-input-section__button {
      &--submit {
        opacity: 1;
        color: $green-light;
      }
    }
  }
}
</style>

<style lang="scss">
.comment-input-section {
  .highlight-item-section--landscape-primary & {
    .comment-input-section__modal {
      left: auto;
      width: 230px;
    }
  }
}
</style>
